import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const services = [
  { label: 'Property Maintenance', href: '/property-maintenance' },
  { label: 'Decks & Fences', href: '/decks-fences' },
  { label: 'Home Renovations', href: '/home-renovations' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleServiceClick = (href: string) => {
    setServicesOpen(false);
    setMobileOpen(false);
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const id = href.replace('/#', '');
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  };

  const navTextClass = scrolled ? 'text-gray-800' : 'text-white';

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement bar */}
      <div className="bg-[#2B5741] text-white py-2 text-xs sm:text-sm font-medium tracking-wide flex items-center justify-between px-4 sm:px-8">
        <div className="flex-1 text-center">
          10% OFF ALL MAINTENANCE PACKAGES BOOKED BEFORE APRIL 15TH &nbsp;✦&nbsp; CALL <a href="tel:9058694676" className="underline hover:text-[#C8A97E] transition-colors">905-869-4676</a>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          <a
            href="https://www.facebook.com/hornpmc"
            rel="nofollow"
            target="_blank"
            aria-label="Horn PMC on Facebook"
            className="w-7 h-7 flex items-center justify-center bg-white/15 rounded-md hover:bg-[#C8A97E] transition-colors"
          >
            <i className="ri-facebook-fill text-xs"></i>
          </a>
          <a
            href="https://www.instagram.com/hornpmc"
            rel="nofollow"
            target="_blank"
            aria-label="Horn PMC on Instagram"
            className="w-7 h-7 flex items-center justify-center bg-white/15 rounded-md hover:bg-[#C8A97E] transition-colors"
          >
            <i className="ri-instagram-line text-xs"></i>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="flex items-center justify-between px-6 lg:px-12 py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <div className="w-10 h-10 flex items-center justify-center bg-[#2B5741] rounded-full">
              <span className="text-white font-bold text-base" style={{ fontFamily: 'Playfair Display, serif' }}>H</span>
            </div>
            <div>
              <p className={`font-bold text-base uppercase tracking-widest leading-tight ${navTextClass} transition-colors duration-300`}
                style={{ fontFamily: 'Playfair Display, serif' }}>
                HORN
              </p>
              <p className={`text-[10px] uppercase tracking-widest leading-none ${scrolled ? 'text-gray-500' : 'text-white/70'} transition-colors duration-300`}>
                Property Maintenance
              </p>
              <p className={`text-[10px] uppercase tracking-widest leading-tight ${scrolled ? 'text-gray-500' : 'text-white/70'} transition-colors duration-300`}>
                &amp; Construction
              </p>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className={`text-sm font-semibold uppercase tracking-wider hover:text-[#C8A97E] transition-colors ${navTextClass}`}>
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen((prev) => !prev)}
                className={`flex items-center gap-1 text-sm font-semibold uppercase tracking-wider hover:text-[#C8A97E] transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}
              >
                Services
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-0 bg-white rounded-md py-2 w-56 mt-1 border border-gray-100">
                  {services.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => handleServiceClick(s.href)}
                      className="block w-full text-left px-5 py-2.5 text-sm text-gray-700 hover:bg-[#2B5741] hover:text-white transition-colors cursor-pointer"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href="#projects" className={`text-sm font-semibold uppercase tracking-wider hover:text-[#C8A97E] transition-colors ${navTextClass}`}>
              Projects
            </a>
            <a href="#contact" className={`text-sm font-semibold uppercase tracking-wider hover:text-[#C8A97E] transition-colors ${navTextClass}`}>
              Contact
            </a>
          </div>

          {/* Phone CTA */}
          <a
            href="tel:9058694676"
            className="hidden lg:flex items-center gap-2 bg-[#2B5741] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#3A7060] transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill text-sm"></i>
            </div>
            905-869-4676
          </a>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${navTextClass}`}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-6">
            <div className="flex flex-col gap-5">
              <Link to="/" onClick={() => setMobileOpen(false)} className="text-gray-800 text-sm font-semibold uppercase tracking-wider">Home</Link>
              <div>
                <p className="text-gray-800 text-sm font-semibold uppercase tracking-wider mb-3">Services</p>
                <div className="flex flex-col gap-2 pl-4">
                  {services.map((s) => (
                    <button
                      key={s.label}
                      onClick={() => handleServiceClick(s.href)}
                      className="text-left text-gray-600 text-sm cursor-pointer hover:text-[#2B5741] transition-colors"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <a href="#projects" onClick={() => setMobileOpen(false)} className="text-gray-800 text-sm font-semibold uppercase tracking-wider">Projects</a>
              <a href="#contact" onClick={() => setMobileOpen(false)} className="text-gray-800 text-sm font-semibold uppercase tracking-wider">Contact</a>
              <a href="tel:9058694676" className="bg-[#2B5741] text-white text-center py-3 rounded-md text-sm font-semibold whitespace-nowrap">
                905-869-4676
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
